import { useEffect, useState } from 'preact/hooks';
import { api } from '../api.js';
import { navigate, listsVersion, showToast, friendlyError } from '../store.js';
import { __, _n } from '../i18n.js';
import Header from '../components/Header.jsx';
import Icon from '../components/Icon.jsx';
import ListEditModal from '../components/ListEditModal.jsx';

/**
 * HH-4: Boodschappen index — two sections (plan §4.2: "is_template lists
 * render in their own section and are never 'shopped' directly"). Live
 * lists first (the daily-use ones), templates below.
 */
function listSubtitle(list) {
  if (list.item_count === 0) {
    return __('Leeg', 'etm');
  }
  if (list.is_template) {
    return _n('%d item', '%d items', list.item_count, 'etm').replace('%d', list.item_count);
  }
  if (list.unchecked_count === 0) {
    return __('Alles afgevinkt', 'etm');
  }
  return _n('%d open', '%d open', list.unchecked_count, 'etm').replace('%d', list.unchecked_count);
}

function ListRow({ list }) {
  return (
    <div
      class="etm-task-list-item"
      role="button"
      tabIndex={0}
      aria-label={list.name}
      onClick={() => navigate('listDetail', { listId: list.id })}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          navigate('listDetail', { listId: list.id });
        }
      }}
    >
      <div class="etm-task-list-item__body">
        <div class="etm-task-list-item__title">
          {list.name}
          {list.is_shared ? <span class="etm-field-hint"> · {__('gedeeld', 'etm')}</span> : null}
        </div>
        <div class="etm-task-list-item__meta">{listSubtitle(list)}</div>
      </div>
    </div>
  );
}

export default function ListsScreen() {
  const [lists, setLists] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const version = listsVersion.value;

  useEffect(() => {
    let cancelled = false;
    api.lists
      .list()
      .then((res) => {
        if (!cancelled) setLists(res.lists || []);
      })
      .catch((err) => {
        if (!cancelled) {
          showToast(friendlyError(err), 'error');
          setLists([]);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [version]);

  if (lists === null) {
    return (
      <>
        <Header title={__('Boodschappen', 'etm')} />
      </>
    );
  }

  const liveLists = lists.filter((l) => !l.is_template);
  const templates = lists.filter((l) => l.is_template);

  return (
    <>
      <Header title={__('Boodschappen', 'etm')} />

      {lists.length === 0 ? (
        <div class="etm-empty-state">
          <span class="icon">
            <Icon name="shopping_cart" size={44} />
          </span>
          {__('Nog geen lijstjes. Maak er een om samen boodschappen bij te houden.', 'etm')}
        </div>
      ) : (
        <>
          <h3>{__('Mijn lijstjes', 'etm')}</h3>
          {liveLists.length === 0 ? (
            <p class="etm-field-hint">{__('Nog geen lijstjes.', 'etm')}</p>
          ) : (
            <div class="etm-task-list">
              {liveLists.map((list) => (
                <ListRow list={list} key={list.id} />
              ))}
            </div>
          )}

          <h3>
            <Icon name="list_alt" size={18} /> {__('Vaste lijsten', 'etm')}
          </h3>
          {templates.length === 0 ? (
            <p class="etm-field-hint">
              {__('Een vaste lijst is handig voor boodschappen die je elke week haalt. Je voegt hem later in één tap toe aan een lijstje.', 'etm')}
            </p>
          ) : (
            <div class="etm-task-list">
              {templates.map((list) => (
                <ListRow list={list} key={list.id} />
              ))}
            </div>
          )}
        </>
      )}

      <button type="button" class="etm-btn etm-btn-primary" onClick={() => setShowCreate(true)}>
        <Icon name="add" size={20} /> {__('Nieuw lijstje', 'etm')}
      </button>

      {showCreate ? (
        <ListEditModal
          editingList={{}}
          templates={templates}
          onClose={() => setShowCreate(false)}
          onSaved={() => setShowCreate(false)}
        />
      ) : null}
    </>
  );
}
